import { Event, type ReadOnlyMetadata } from '@tix/entity'
import type { Schema } from '@tix/openapi-sdk'

import type { GalleryImage } from '@/types/images'

import { getAssetUrl, getAssetUrlFromApiUrl } from './assets'

type FlowImages = Schema<'flow_definition_min'>['meta']['images']

export function formatGalleryImages(images: FlowImages): GalleryImage[] {
  if (!images || images.length === 0) {
    return []
  }

  return images.map((image) => ({
    src: getAssetUrl(image.asset_id),
    alt: image.alt_text,
    noCrop: image.no_crop,
  }))
}

/**
 * Extracts the image gallery from event template metadata and formats it for
 * display. Falls back to the single `image_profile` asset when no gallery has
 * been configured.
 */
export function formatEventGalleryImages(meta: ReadOnlyMetadata): GalleryImage[] {
  const galleryRaw = meta.get('gallery_images')

  if (galleryRaw) {
    try {
      const parsed: unknown = JSON.parse(galleryRaw)
      if (Array.isArray(parsed)) {
        const gallery = parsed
          .filter((image): image is Schema<'image_ref'> => typeof image?.asset_id === 'string')
          .map((image) => ({
            src: getAssetUrl(image.asset_id),
            alt: image.alt_text,
            noCrop: image.no_crop,
          }))

        if (gallery.length) return gallery
      }
    } catch {
      /* fall through to profile image */
    }
  }

  const profileUrl = meta.get('image_profile')
  if (!profileUrl) return []

  return [
    {
      src: getAssetUrlFromApiUrl(profileUrl),
      alt: meta.get('image_profile_alt_text'),
      noCrop: meta.get('image_profile_no_cropping') === 'true',
    },
  ]
}

export type EventImage = {
  src: string
  alt: string
  noCrop: boolean
}

export function imageData(event: Event, type: 'teaser' | 'cover' | 'cart'): EventImage | undefined {
  const imageType = type === 'cover' ? 'image_cover' : 'image_profile'
  const size = type === 'teaser' ? 578 : type === 'cover' ? 1500 : 64

  const metaSrc = event.metadata.get(imageType)

  if (!metaSrc) return undefined

  return {
    src: getAssetUrlFromApiUrl(metaSrc, size),
    alt: event.metadata.get(`${imageType}_alt_text`) ?? `Image for ${event.data.name}`,
    noCrop: event.metadata.get(`${imageType}_no_cropping`) === 'true' ? true : false,
  }
}
